import { useState } from 'react';
import { useForgotPassword } from '@/lib/auth/useForgotPassword';
import { SheetBackButton, FieldErrorText } from './auth-form-ui';
import { fieldInputClasses, primaryCtaClasses, isValidEmail, INVALID_EMAIL_MESSAGE } from './auth-form-style';

const TEST_ID_PREFIX = 'auth-forgot-password';

export interface AuthForgotPasswordFormProps {
  /** Switch back to the login form. */
  onBackToLogin: () => void;
  /** Switch to the signup funnel ("Don't have an account yet? Sign up"). */
  onSignup?: () => void;
}

/**
 * Reset-password sheet (ARC-145 design) — collects an email and asks the backend
 * to send a branded reset link (`POST /auth/forgot-password`).
 *
 * The backend is enumeration-safe (always 204), so on success we show the same
 * confirmation regardless of whether the address has an account — the UI must never
 * disclose which emails are registered.
 */
export function AuthForgotPasswordForm({ onBackToLogin, onSignup }: AuthForgotPasswordFormProps) {
  const forgotPassword = useForgotPassword();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [emailInvalid, setEmailInvalid] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    setEmailInvalid(false);

    // Styled field error per the ARC-145 designs — the form is noValidate so
    // the browser's native tooltip never appears.
    if (!isValidEmail(email)) {
      setEmailInvalid(true);
      return;
    }

    setLoading(true);
    try {
      await forgotPassword({ email });
      setSent(true);
    } catch {
      // A failure here is a transport/server error, not "email not found" (that path
      // still returns 204) — so a generic retry message is the right call.
      setError('Something went wrong sending your reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <div data-testid={`${TEST_ID_PREFIX}-sent`} className="relative flex flex-col gap-5 w-full max-w-[377px] mx-auto py-6">
        <SheetBackButton testId={`${TEST_ID_PREFIX}-back-chevron`} onClick={onBackToLogin} />
        <div className="flex flex-col gap-3 text-center">
          <h2 className="text-2xl font-bold text-ink">Check your email</h2>
          <p className="text-base text-ink">
            If an account exists for <span className="font-bold">{email}</span>, we&apos;ve sent a link to reset your
            password. The link expires soon, so use it shortly.
          </p>
          <div className="flex flex-col items-center gap-1.5 mt-2">
            <button
              data-testid={`${TEST_ID_PREFIX}-back-button`}
              type="button"
              className="text-purple font-semibold underline text-sm"
              onClick={onBackToLogin}
            >
              Back to log in
            </button>
            <button
              data-testid={`${TEST_ID_PREFIX}-resend-button`}
              type="button"
              className="text-sm text-muted underline"
              onClick={() => {
                setSent(false);
                setError('');
              }}
            >
              Use a different email
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={TEST_ID_PREFIX} className="relative flex flex-col gap-5 w-full max-w-[377px] mx-auto py-6">
      <SheetBackButton testId={`${TEST_ID_PREFIX}-back-chevron`} onClick={onBackToLogin} />

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
        <h2 className="text-2xl font-bold text-ink text-center">Reset Password</h2>
        <p className="text-base text-ink text-center max-w-[320px] mx-auto">
          Enter the email associated with your account and we will send you password reset instructions.
        </p>

        {error && (
          <p data-testid={`${TEST_ID_PREFIX}-error`} role="alert" className="text-danger-alt text-xs px-3">
            {error}
          </p>
        )}

        <div className="flex flex-col">
          <input
            data-testid={`${TEST_ID_PREFIX}-email-input`}
            id="forgot-sheet-email"
            type="email"
            aria-label="Email"
            aria-invalid={emailInvalid}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            disabled={loading}
            className={fieldInputClasses(emailInvalid)}
          />
          {emailInvalid && <FieldErrorText testId={`${TEST_ID_PREFIX}-email-error`}>{INVALID_EMAIL_MESSAGE}</FieldErrorText>}
        </div>

        <div className="flex flex-col items-center gap-2 mt-1">
          <button
            data-testid={`${TEST_ID_PREFIX}-submit-button`}
            type="submit"
            disabled={loading}
            className={primaryCtaClasses}
          >
            {loading ? 'Sending…' : 'Reset Password'}
          </button>
          <p className="text-sm text-ink">
            Don&apos;t have an account yet?{' '}
            <button
              data-testid={`${TEST_ID_PREFIX}-signup-button`}
              type="button"
              className="text-purple font-semibold underline"
              onClick={onSignup ?? onBackToLogin}
            >
              Sign up
            </button>
          </p>
        </div>
      </form>
    </div>
  );
}
